import { useEffect, useRef } from "react";
import { callRecoverStuck } from "@/lib/functions";
import type { PendingSubmit } from "@/lib/types";

const STUCK_THRESHOLD_MS = 30_000;
const THROTTLE_MS = 60_000;

/**
 * Detects pendingSubmit entries that have been past their expiresAt for more
 * than 30s and asks the server to recover. Idempotent on the server side; we
 * additionally throttle calls per-tab to avoid hammering the function during
 * temporary connectivity issues.
 */
export const useStuckSessionRecovery = (
  sessionId: string | null,
  pendingSubmit: PendingSubmit | null
): void => {
  const lastCallRef = useRef<number>(0);

  useEffect(() => {
    if (!sessionId || !pendingSubmit) return;

    const tick = () => {
      const now = Date.now();
      const stuckFor = now - pendingSubmit.expiresAt;
      if (stuckFor < STUCK_THRESHOLD_MS) return;
      if (now - lastCallRef.current < THROTTLE_MS) return;
      lastCallRef.current = now;
      callRecoverStuck({ sessionId, pendingId: pendingSubmit.id }).catch(() => {});
    };

    tick();
    const id = window.setInterval(tick, 5_000);
    return () => clearInterval(id);
  }, [sessionId, pendingSubmit?.id, pendingSubmit?.expiresAt]);
};
